import React from 'react'
import styled from 'styled-components'
import {Dialog as MuiDialog, DialogProps as MuiDialogProps} from '@mui/material'

// Styled Dialog Components
export const StyledDialogTitle = styled.div`
  padding: 24px 24px 16px;
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  line-height: 28px;
  color: var(--text-primary, #111827);
  border-bottom: 1px solid var(--border-default, #e5e7eb);
`

export const StyledDialogContent = styled.div`
  padding: 24px;
  overflow-y: auto;
  color: var(--text-primary, #111827);

  /* Remove default margin from first/last children */
  > *:first-child {
    margin-top: 0;
  }

  > *:last-child {
    margin-bottom: 0;
  }
`

export const StyledDialogActions = styled.div`
  padding: 16px 24px 24px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  border-top: 1px solid var(--border-default, #e5e7eb);
  background-color: var(--background-secondary, #f9fafb);

  /* Responsive stacking on small screens */
  @media (max-width: 480px) {
    flex-direction: column-reverse;

    & > * {
      width: 100%;
    }
  }
`

// Wrapper for the MUI Dialog with custom styling
export const StyledDialog = styled(MuiDialog)`
  .MuiDialog-paper {
    border-radius: 8px;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
    background-color: var(--background-primary, #ffffff);

    /* Ensure proper max width */
    max-width: min(90vw, 600px);
    width: 100%;
    margin: 32px;

    @media (max-width: 600px) {
      margin: 16px;
      max-width: calc(100vw - 32px);
    }
  }

  .MuiBackdrop-root {
    background-color: rgba(0, 0, 0, 0.5);
  }
`

// Props interfaces
export interface StyledDialogTitleProps {
  children: React.ReactNode
  className?: string
}

export interface StyledDialogContentProps {
  children: React.ReactNode
  className?: string
}

export interface StyledDialogActionsProps {
  children: React.ReactNode
  className?: string
}

// Export the styled components as the default way to use dialogs
export const DialogTitle: React.FC<StyledDialogTitleProps> = ({children, className}) => (
  <StyledDialogTitle className={className}>{children}</StyledDialogTitle>
)

export const DialogContent: React.FC<StyledDialogContentProps> = ({children, className}) => (
  <StyledDialogContent className={className}>{children}</StyledDialogContent>
)

export const DialogActions: React.FC<StyledDialogActionsProps> = ({children, className}) => (
  <StyledDialogActions className={className}>{children}</StyledDialogActions>
)

// Props interface for our Dialog component
export interface DialogProps extends MuiDialogProps {
  children: React.ReactNode
}

// Re-export the styled Dialog as the main Dialog component with proper typing
export const Dialog: React.FC<DialogProps> = StyledDialog

export default {
  Dialog: StyledDialog,
  DialogTitle,
  DialogContent,
  DialogActions,
}
